import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';

interface PageProps {
  title?: string;
  scrollToTop?: boolean;
}

interface PagePropertiesContextProps {
  setPageProperties: (props: PageProps) => void;
}

interface PagePropertiesProviderProps {
  defaultPageTitle: string;
}

const PagePropertiesContext = createContext<
  PagePropertiesContextProps | undefined
>(undefined);

const ScrollToTopEffect: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);
  return null;
};

const PagePropertiesProvider: React.FC<
  PropsWithChildren<PagePropertiesProviderProps>
> = ({ defaultPageTitle, children }) => {
  const [pageProperties, setPageProperties] = useState<PageProps>({});
  const pageTitle = pageProperties.title
    ? `Airship: ${pageProperties.title}`
    : defaultPageTitle;

  return (
    <PagePropertiesContext.Provider value={{ setPageProperties }}>
      <title>{pageTitle}</title>
      {children}
    </PagePropertiesContext.Provider>
  );
};

const withPageProps = <P extends object>(
  Component: React.ComponentType<P>,
  pageProps: PageProps
) => {
  return (props: P) => {
    const context = useContext(PagePropertiesContext);
    if (!context) {
      throw new Error(
        'withPageProps must be used within a PagePropertiesProvider'
      );
    }

    const { setPageProperties } = context;

    useEffect(() => {
      setPageProperties(pageProps);

      return () => {
        setPageProperties({ title: undefined });
      };
    }, [setPageProperties]);

    return (
      <>
        <Component {...props} />
        {pageProps.scrollToTop && <ScrollToTopEffect />}
      </>
    );
  };
};

export { PagePropertiesProvider, withPageProps };
